import React from 'react';
import { Link } from 'react-router-dom';
import {
    education,
    experience,
    profile,
} from '../data/resume.js';
import { projects } from '../data/projects.js';
import {
    ArrowRightIcon,
    ExternalIcon,
    GitHubIcon,
    LinkedInIcon,
    MailIcon,
    PhoneIcon,
} from '../components/Icons.js';
import '../styles/home.css';

const SectionHeading = ({ title }) => (
    <div className="section-heading">
        <h2>{title}</h2>
    </div>
);

const Tags = ({ items }) => (
    <ul className="tags">
        {items.map((t) => (
            <li key={t}>{t}</li>
        ))}
    </ul>
);

const Hero = () => (
    <section id="about" className="hero">
        <div className="container">
            <div className="hero-copy">
                <div className="avatar">
                    {profile.photo ? (
                        <img src={profile.photo} alt={profile.name} />
                    ) : (
                        <span aria-hidden="true">SC</span>
                    )}
                </div>
                <h1>{profile.name}</h1>
                <p className="hero-role">{profile.role}</p>
                <div className="button-row">
                    <a className="button button-primary" href={profile.resumePdf} target="_blank" rel="noopener noreferrer">
                        <ExternalIcon /> Resume
                    </a>
                    <a className="button" href={profile.github} target="_blank" rel="noopener noreferrer">
                        <GitHubIcon /> GitHub
                    </a>
                    <a className="button" href={profile.linkedin} target="_blank" rel="noopener noreferrer">
                        <LinkedInIcon /> LinkedIn
                    </a>
                    <a className="button" href={`mailto:${profile.email}`}>
                        <MailIcon /> Email
                    </a>
                    <a className="button" href={profile.phoneHref}>
                        <PhoneIcon /> {profile.phone}
                    </a>
                </div>
            </div>
        </div>
    </section>
);

const Education = () => (
    <section id="education" className="section">
        <div className="container">
            <SectionHeading title="Education" />
            <ul className="edu-list">
                {education.map((e) => (
                    <li key={e.degree}>
                        <div>
                            <h3>{e.degree}, {e.field}</h3>
                            <p className="edu-school">{e.school} · {e.location}</p>
                        </div>
                        <p className="edu-when">
                            <span>{e.period}</span> · GPA <span>{e.gpa}</span>
                        </p>
                    </li>
                ))}
            </ul>
        </div>
    </section>
);

const Experience = () => (
    <section id="experience" className="section section-alt">
        <div className="container">
            <SectionHeading title="Experience" />
            <ol className="timeline">
                {experience.map((job) => (
                    <li key={job.company} className="timeline-item">
                        <div className="timeline-when">
                            <span className="meta">{job.period}</span>
                            <span className="meta muted">{job.location}</span>
                        </div>
                        <article className="card job-card">
                            <h3>{job.title}</h3>
                            <p className="job-company">{job.company}</p>
                            <ul className="bullets">
                                {job.bullets.map((b) => (
                                    <li key={b}>{b}</li>
                                ))}
                            </ul>
                            <Tags items={job.tags} />
                        </article>
                    </li>
                ))}
            </ol>
        </div>
    </section>
);

const Projects = () => (
    <section id="projects" className="section">
        <div className="container">
            <SectionHeading title="Projects" />
            <div className="project-grid">
                {projects.map((p) => (
                    <article key={p.slug} className="card project-card">
                        <Link to={`/projects/${p.slug}`} className="project-cover" tabIndex={-1} aria-hidden="true">
                            <img src={p.cover} alt="" loading="lazy" />
                        </Link>
                        <div className="project-body">
                            <h3>
                                <Link to={`/projects/${p.slug}`}>{p.title}</Link>
                            </h3>
                            <p className="project-subtitle">{p.subtitle}</p>
                            <p className="project-summary">{p.summary}</p>
                            <Tags items={p.stack} />
                            <div className="project-actions">
                                <Link to={`/projects/${p.slug}`} className="text-link" aria-label={`Open ${p.title}`}>
                                    <ArrowRightIcon size={20} />
                                </Link>
                                <a
                                    href={p.github}
                                    className="icon-button"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    aria-label={`${p.title} on GitHub`}
                                >
                                    <GitHubIcon />
                                </a>
                            </div>
                        </div>
                    </article>
                ))}
            </div>
        </div>
    </section>
);

const Home = () => (
    <>
        <Hero />
        <Education />
        <Experience />
        <Projects />
    </>
);

export default Home;
